import { toLocalInput } from '@/lib/date-input';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BLOG_LIMITS, type BlogPostAdminDto, type BlogPostInput, type BlogPostStatus, type BlogPostText } from '@ecom/contracts';
import { Choices, ToggleField } from '@/components/fields';
import { TrashIcon } from '@/components/icons';
import { DetailLayout, Page, SaveBar, Section, Segmented } from '@/components/page';
import { ProductPicker } from '@/components/product-picker';
import { Alert, Button, ConfirmDialog, Field, Input, Pill, Skeleton, Textarea } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { RichTextEditor } from '@/pages/content-pages/editor';
import { MediaField } from '@/pages/home-layout/fields';
import { useBlogStore } from '@/stores/blog.store';
import { postState } from './index';

type Language = 'ar' | 'en';

interface Text {
  title: string;
  excerpt: string;
  body: string;
  metaTitle: string;
  metaDescription: string;
}

interface Values {
  ar: Text;
  en: Text;
  slug: string;
  status: BlogPostStatus;
  /** `YYYY-MM-DDTHH:mm`, the browser's own time; '' for none. */
  publishedAt: string;
  cover: { key: string; url: string } | undefined;
  productIds: string[];
  /** Kept out of search engines (ADR-095). */
  noindex: boolean;
}

const EMPTY_TEXT: Text = { title: '', excerpt: '', body: '', metaTitle: '', metaDescription: '' };

function textFrom(text: BlogPostText | null): Text {
  return text
    ? { title: text.title, excerpt: text.excerpt ?? '', body: text.body, metaTitle: text.metaTitle ?? '', metaDescription: text.metaDescription ?? '' }
    : { ...EMPTY_TEXT };
}

function valuesFrom(post: BlogPostAdminDto | null): Values {
  return {
    ar: textFrom(post?.content.ar ?? null),
    en: textFrom(post?.content.en ?? null),
    slug: post?.slug ?? '',
    status: post?.status ?? 'DRAFT',
    publishedAt: toLocalInput(post?.publishedAt ?? null),
    cover: post?.cover ?? undefined,
    productIds: post?.productIds ?? [],
    noindex: post?.noindex ?? false,
  };
}

/** The request the form makes: a language goes when anything of it is written. */
function inputFrom(values: Values): BlogPostInput {
  const text = (value: Text): BlogPostText | undefined =>
    value.title.trim() || value.body.trim()
      ? {
          title: value.title.trim(),
          excerpt: value.excerpt.trim() || null,
          body: value.body,
          metaTitle: value.metaTitle.trim() || null,
          metaDescription: value.metaDescription.trim() || null,
        }
      : undefined;
  const ar = text(values.ar);
  const en = text(values.en);
  return {
    translations: { ...(ar ? { ar } : {}), ...(en ? { en } : {}) },
    ...(values.slug.trim() ? { slug: values.slug.trim() } : {}),
    status: values.status,
    publishedAt: values.publishedAt ? new Date(values.publishedAt).toISOString() : null,
    coverKey: values.cover?.key ?? null,
    productIds: values.productIds,
    noindex: values.noindex,
  };
}

/** A post (ADR-080): written as pages are, in each language, with its cover, its date and its products. */
export function BlogEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { current, currentLoading, saveError, open, clearCurrent } = useBlogStore();

  useEffect(() => {
    if (isNew) clearCurrent();
    else void open(id);
  }, [id, isNew, open, clearCurrent]);

  if (!isNew && (!current || current.id !== id)) {
    // Loading, or the post could not be read — then say why.
    return currentLoading || !saveError ? <Skeleton className="m-6 h-96" /> : <Alert>{saveError}</Alert>;
  }
  return <PostForm key={isNew ? 'new' : current?.id} post={isNew ? null : current} />;
}

function PostForm({ post }: { post: BlogPostAdminDto | null }) {
  const t = useT();
  const navigate = useNavigate();
  const adminLocale = useUiStore((state) => state.locale);
  const { saving, saveError, save, remove } = useBlogStore();
  const initial = useMemo(() => valuesFrom(post), [post]);
  const [values, setValues] = useState<Values>(initial);
  const [language, setLanguage] = useState<Language>(adminLocale === 'en' ? 'en' : 'ar');
  const [deleting, setDeleting] = useState(false);
  const dirty = JSON.stringify(values) !== JSON.stringify(initial);
  const text = values[language];
  const setText = (patch: Partial<Text>) => setValues((current) => ({ ...current, [language]: { ...current[language], ...patch } }));

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    const saved = await save(post?.id ?? null, inputFrom(values));
    if (saved && !post) navigate(`/blog/${saved.id}`, { replace: true });
  }

  const state = post ? postState(post) : null;

  return (
    <Page
      title={post ? post.title : t('blogWrite')}
      back={{ to: '/blog', label: t('blog') }}
      meta={state ? <Pill tone={state === 'live' ? 'success' : state === 'scheduled' ? 'info' : 'neutral'}>{t(`blogState_${state}`)}</Pill> : null}
      actions={
        post ? (
          <Button variant="secondary" className="text-danger" onClick={() => setDeleting(true)}>
            <TrashIcon className="size-4" />
            {t('delete')}
          </Button>
        ) : null
      }
      width="wide"
    >
      <form id="blog-form" onSubmit={submit} className="contents">
        <DetailLayout
          main={
            <>
              <Section
                title={t('blogWords')}
                action={
                  <Segmented
                    label={t('blogLanguage')}
                    items={[
                      { id: 'ar', label: 'العربية' },
                      { id: 'en', label: 'English' },
                    ]}
                    value={language}
                    onChange={setLanguage}
                  />
                }
              >
                <div className="flex flex-col gap-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                  <Field label={t('blogPostTitle')} htmlFor="post-title">
                    <Input id="post-title" value={text.title} maxLength={BLOG_LIMITS.title} onChange={(event) => setText({ title: event.target.value })} />
                  </Field>
                  <Field label={t('blogExcerpt')} htmlFor="post-excerpt" hint={t('blogExcerptHint')}>
                    <Textarea
                      id="post-excerpt"
                      rows={2}
                      value={text.excerpt}
                      maxLength={BLOG_LIMITS.excerpt}
                      onChange={(event) => setText({ excerpt: event.target.value })}
                    />
                  </Field>
                  <RichTextEditor
                    key={language}
                    label={t('blogBody')}
                    language={language}
                    title={text.title}
                    links={[]}
                    value={text.body}
                    onChange={(body) => setText({ body })}
                  />
                </div>
              </Section>

              <Section title={t('blogSearchResult')} subtitle={t('blogSearchResultHint')}>
                <div className="flex flex-col gap-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                  <Field label={t('metaTitle')} htmlFor="post-meta-title">
                    <Input
                      id="post-meta-title"
                      value={text.metaTitle}
                      placeholder={text.title}
                      maxLength={BLOG_LIMITS.metaTitle}
                      onChange={(event) => setText({ metaTitle: event.target.value })}
                    />
                  </Field>
                  <Field label={t('metaDescription')} htmlFor="post-meta-description">
                    <Textarea
                      id="post-meta-description"
                      rows={2}
                      value={text.metaDescription}
                      placeholder={text.excerpt}
                      maxLength={BLOG_LIMITS.metaDescription}
                      onChange={(event) => setText({ metaDescription: event.target.value })}
                    />
                  </Field>
                  <ToggleField
                    label={t('seoHideLabel')}
                    hint={t('seoHideHint')}
                    checked={values.noindex}
                    onChange={(noindex) => setValues((current) => ({ ...current, noindex }))}
                  />
                </div>
              </Section>

              <Section title={t('blogProducts')} subtitle={t('blogProductsHint')}>
                <ProductPicker
                  ordered
                  label={t('blogProducts')}
                  selectedIds={values.productIds}
                  onChange={(productIds) => setValues((current) => ({ ...current, productIds: productIds.slice(0, BLOG_LIMITS.products) }))}
                />
              </Section>
            </>
          }
          aside={
            <>
              <Section title={t('blogPublishing')}>
                <div className="flex flex-col gap-4">
                  <Choices
                    legend={t('blogStatus')}
                    legendHidden
                    name="post-status"
                    value={values.status}
                    onChange={(status) => setValues((current) => ({ ...current, status }))}
                    options={[
                      { value: 'DRAFT', label: t('blogState_draft'), hint: t('blogDraftHint') },
                      { value: 'PUBLISHED', label: t('blogPublish'), hint: t('blogPublishHint') },
                    ]}
                  />
                  <Field label={t('blogDate')} htmlFor="post-date" hint={t('blogDateHint')}>
                    <Input
                      id="post-date"
                      type="datetime-local"
                      dir="ltr"
                      value={values.publishedAt}
                      onChange={(event) => setValues((current) => ({ ...current, publishedAt: event.target.value }))}
                    />
                  </Field>
                  <Field label={t('urlHandle')} htmlFor="post-slug" hint={t('blogSlugHint')}>
                    <Input
                      id="post-slug"
                      dir="ltr"
                      value={values.slug}
                      placeholder="leather-wallet-care"
                      onChange={(event) => setValues((current) => ({ ...current, slug: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                    />
                  </Field>
                </div>
              </Section>
              <Section title={t('blogCover')}>
                <MediaField
                  kind="image"
                  label={t('blogCover')}
                  hint={t('blogCoverHint')}
                  portrait={false}
                  value={values.cover}
                  onChange={(cover) => setValues((current) => ({ ...current, cover: cover as Values['cover'] }))}
                />
              </Section>
            </>
          }
        />
      </form>

      <SaveBar
        saving={saving}
        dirty={!post || dirty}
        form="blog-form"
        message={saveError ? <Alert>{saveError}</Alert> : undefined}
        onDiscard={post && dirty ? () => setValues(initial) : undefined}
      />

      <ConfirmDialog
        open={deleting}
        title={t('blogDeleteTitle')}
        body={t('blogDeleteBody')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (post && (await remove(post.id))) navigate('/blog', { replace: true });
          setDeleting(false);
        }}
        onCancel={() => setDeleting(false)}
      />
    </Page>
  );
}
